/**
 * ZeroPaidCheckbox.jsx
 *
 * A small affordance inside the Payment drawer that lets the operator mark
 * a bill as "$0 paid" (no payment needed). This handles the operator-
 * codified R1 pattern: when a bill's CSV status is `Paid` but the Amount
 * Paid / BillPortal / Anchor columns are all $0, the bill had no due amount
 * (e.g. credits covered it).
 *
 * When checked, the parent should disable the Transaction Reference / Amount
 * inputs and call POST /api/payments/v2/files/{pf_id}/mark-paid-zero on save
 * instead of /record-payment.
 *
 * Props:
 *   checked       boolean
 *   onChange      (boolean) => void
 *   reason        string  (controlled) — the explanation entered by the user
 *   onReasonChange (string) => void
 *   showReason    boolean — show the inline reason input (default true)
 *   testId        string
 */
import { Info } from 'lucide-react';
import { Input } from '../ui/input';

export default function ZeroPaidCheckbox({
  checked = false,
  onChange,
  reason = '',
  onReasonChange,
  showReason = true,
  testId = 'zero-paid-checkbox',
}) {
  return (
    <div
      className={`border rounded-md p-3 transition-colors ${
        checked ? 'border-blue-300 bg-blue-50/40' : 'border-dashed border-muted-foreground/30'
      }`}
      data-testid={testId}
    >
      <label className="flex items-start gap-2 text-xs cursor-pointer select-none">
        <input
          type="checkbox"
          checked={!!checked}
          onChange={(e) => onChange?.(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
          data-testid={`${testId}-input`}
        />
        <span className="flex-1">
          <strong className="text-sm font-semibold text-blue-700">$0 paid (no payment needed)</strong>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Mark this bill as paid without recording a transaction. Use when
            the bill has $0 due or credits already cover it.
          </p>
        </span>
      </label>

      {checked && showReason && (
        <div className="mt-3 space-y-1 pl-6" data-testid={`${testId}-reason-block`}>
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            Reason (optional)
          </label>
          <Input
            value={reason}
            onChange={(e) => onReasonChange?.(e.target.value)}
            placeholder="e.g. Account had a credit balance"
            className="h-8 text-xs"
            data-testid={`${testId}-reason`}
          />
          <p className="text-[10px] text-muted-foreground flex items-start gap-1 mt-1">
            <Info className="h-3 w-3 mt-0.5 shrink-0" />
            On save, bill will be flagged paid with amount $0 and no transaction inserted.
          </p>
        </div>
      )}
    </div>
  );
}
